import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import DateField from "@/components/DateField";
import ConfirmDialog from "@/components/ConfirmDialog";

/**
 * 16-WO-SPH-DATE-UX-R1 — Simplified SPH history UX.
 *
 * Each submission: { id?, date: "YYYY-MM-DD", note: string,
 *                     created_by_name?, created_at? }
 *
 * Behaviours:
 *  - "+ Tambah SPH" reveals an inline mini form (Tanggal default = hari ini,
 *    Catatan opsional, Simpan/Batal). No empty row is appended to the list
 *    until Simpan is clicked.
 *  - Date input uses <DateField> → Indonesian dd/mm/yyyy display, manual
 *    typing OR calendar. Invalid dates are rejected on commit.
 *  - Rows sorted DESC (terbaru di atas).
 *  - Hapus meminta konfirmasi.
 *  - Note edit di-inline pada row (setiap baris memiliki input note yang
 *    langsung tersinkron ke `onChange` — supaya user bisa memperbaharui catatan
 *    revisi tanpa perlu rebuild seluruh entry). Date rows lama TIDAK bisa
 *    diedit inline (immutable per spec — hanya bisa ditambah / dihapus).
 */
export default function SphSubmissionsList({ value, onChange, testidPrefix = "sph" }) {
  const rows = useMemo(() => {
    const arr = Array.isArray(value) ? value.map((r) => ({ ...r })) : [];
    // Sort DESC by date, then by created_at as tie-breaker
    arr.sort((a, b) => {
      const da = a.date || "";
      const db = b.date || "";
      if (da !== db) return db.localeCompare(da);
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      return cb.localeCompare(ca);
    });
    return arr;
  }, [value]);

  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState(todayISO());
  const [newNote, setNewNote] = useState("");
  const [newOffer, setNewOffer] = useState("");
  const [newStatus, setNewStatus] = useState("sent");
  const [confirmDel, setConfirmDel] = useState(null); // row object

  const startAdd = () => {
    setNewDate(todayISO());
    setNewNote("");
    setNewOffer("");
    setNewStatus("sent");
    setAdding(true);
  };
  const cancelAdd = () => {
    setAdding(false);
    setNewDate("");
    setNewNote("");
    setNewOffer("");
    setNewStatus("sent");
  };
  const commitAdd = () => {
    const d = (newDate || "").trim();
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return; // invalid — abort silently, DateField already reverts
    const next = [
      ...rows.map(stripUi),
      { date: d, note: (newNote || "").trim(),
        offer_value: newOffer === "" ? null : Number(newOffer),
        status: newStatus || "sent" },
    ];
    onChange(next);
    cancelAdd();
  };

  const updateNote = (row, note) => {
    const next = rows.map((r) => (r === row ? { ...r, note } : r)).map(stripUi);
    onChange(next);
  };
  const updateStatus = (row, status) => {
    const next = rows.map((r) => (r === row ? { ...r, status } : r)).map(stripUi);
    onChange(next);
  };

  const doDelete = () => {
    if (!confirmDel) return;
    const target = confirmDel;
    const next = rows.filter((r) => r !== target).map(stripUi);
    onChange(next);
    setConfirmDel(null);
  };

  return (
    <div className="space-y-1.5" data-testid={`${testidPrefix}-submissions-list`}>
      <div className="flex items-center justify-between">
        <Label className="overline text-slate-600">
          Riwayat Submit SPH
          <span className="text-slate-400 italic normal-case text-[10px] ml-1">
            (terbaru di atas · dd/mm/yyyy)
          </span>
        </Label>
        {!adding && (
          <button
            type="button"
            data-testid={`${testidPrefix}-submissions-add`}
            onClick={startAdd}
            className="text-[10px] uppercase tracking-wider font-semibold text-[#FF5A1F] hover:text-[#e04a13] border border-[#FF5A1F] px-2 py-1"
          >
            + Tambah SPH
          </button>
        )}
      </div>

      {adding && (
        <div
          data-testid={`${testidPrefix}-submissions-add-form`}
          className="border border-[#FF5A1F] bg-orange-50/40 p-2.5 space-y-2"
        >
          <div className="grid grid-cols-[140px_140px_1fr] gap-2 items-start">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Tanggal SPH</div>
              <DateField
                testid={`${testidPrefix}-submissions-add-date`}
                value={newDate}
                onChange={setNewDate}
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Nilai Penawaran <span className="italic normal-case text-slate-400">(Rp)</span></div>
              <Input
                data-testid={`${testidPrefix}-submissions-add-offer`}
                type="number"
                value={newOffer}
                onChange={(e) => setNewOffer(e.target.value)}
                placeholder="0"
                className="rounded-none h-9 text-xs tabular-nums"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                Catatan revisi/versi <span className="italic normal-case text-slate-400">(opsional)</span>
              </div>
              <Input
                data-testid={`${testidPrefix}-submissions-add-note`}
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="mis. Rev-2 setelah revisi harga"
                className="rounded-none h-9 text-xs"
              />
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Status Submission</div>
            <select
              data-testid={`${testidPrefix}-submissions-add-status`}
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="h-9 border border-slate-300 px-2 text-xs bg-white focus:outline-none focus:border-[#FF5A1F] rounded-none"
            >
              <option value="sent">SPH Dikirim</option>
              <option value="revised">Negosiasi / Revisi</option>
              <option value="approved">Verifikasi — Menunggu PO</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              data-testid={`${testidPrefix}-submissions-add-cancel`}
              onClick={cancelAdd}
              className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-slate-800 border border-slate-300 px-3 py-1.5"
            >
              Batal
            </button>
            <button
              type="button"
              data-testid={`${testidPrefix}-submissions-add-save`}
              onClick={commitAdd}
              disabled={!newDate || !/^\d{4}-\d{2}-\d{2}$/.test(newDate)}
              className="text-[10px] uppercase tracking-wider font-semibold text-white bg-[#FF5A1F] hover:bg-[#e04a13] px-3 py-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Simpan
            </button>
          </div>
        </div>
      )}

      {rows.length === 0 && !adding && (
        <p className="text-[11px] text-slate-400 italic border border-dashed border-slate-200 px-3 py-2">
          Belum ada submission SPH. Klik "+ Tambah SPH" untuk mencatat submission pertama beserta catatannya.
        </p>
      )}

      {rows.length > 0 && (
        <div className="space-y-1.5">
          {rows.map((r, i) => {
            const version = rows.length - i; // rows sorted DESC, latest first
            return (
            <div
              key={r.id || `${r.date}-${i}`}
              data-testid={`${testidPrefix}-submissions-row-${i}`}
              className="border border-slate-200 bg-slate-50 p-2 space-y-1.5"
            >
              <div className="grid grid-cols-[70px_110px_130px_1fr_auto] gap-2 items-start">
                <div
                  className="h-8 flex items-center px-2 border border-slate-200 bg-white text-xs font-bold text-[#FF5A1F]"
                  data-testid={`${testidPrefix}-submissions-version-${i}`}
                  title={`Versi #${version}`}
                >v{version}</div>
                <div
                  data-testid={`${testidPrefix}-submissions-date-${i}`}
                  className="h-8 flex items-center px-2 border border-slate-200 bg-white text-xs tabular-nums text-[#0B1D3A] font-semibold"
                  title="Tanggal SPH tidak dapat diedit — hapus dan tambah ulang bila perlu"
                >
                  {isoToDMY(r.date) || "—"}
                </div>
                <select
                  data-testid={`${testidPrefix}-submissions-status-${i}`}
                  value={r.status || "sent"}
                  onChange={(e) => updateStatus(r, e.target.value)}
                  className="h-8 border border-slate-200 bg-white px-2 text-[11px] focus:outline-none focus:border-[#FF5A1F] rounded-none"
                >
                  <option value="sent">SPH Dikirim</option>
                  <option value="revised">Negosiasi / Revisi</option>
                  <option value="approved">Verifikasi — Menunggu PO</option>
                </select>
                <Input
                  value={r.note || ""}
                  onChange={(e) => updateNote(r, e.target.value)}
                  placeholder="Catatan revisi/versi (opsional)"
                  data-testid={`${testidPrefix}-submissions-note-${i}`}
                  className="rounded-none h-8 text-xs"
                />
                <button
                  type="button"
                  onClick={() => setConfirmDel(r)}
                  data-testid={`${testidPrefix}-submissions-remove-${i}`}
                  className="text-[10px] uppercase tracking-wider font-semibold text-rose-600 hover:text-rose-800 border border-rose-200 px-2 py-1 h-8"
                >
                  Hapus
                </button>
              </div>
              {r.offer_value != null && r.offer_value !== "" && (
                <div className="text-[10px] text-slate-600 tabular-nums" data-testid={`${testidPrefix}-submissions-offer-${i}`}>
                  Penawaran: Rp {Number(r.offer_value).toLocaleString("id-ID")}
                </div>
              )}
              {r.created_by_name && (
                <div className="text-[10px] text-slate-500 italic">
                  Dicatat oleh {r.created_by_name}
                  {r.created_at ? ` • ${new Date(r.created_at).toLocaleString("id-ID")}` : ""}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-slate-500">
        Field <span className="font-semibold">SPH Submit Date (latest)</span> di atas otomatis mengikuti tanggal terbaru dari daftar ini.
      </p>

      <ConfirmDialog
        open={!!confirmDel}
        onOpenChange={(o) => !o && setConfirmDel(null)}
        title="Hapus Riwayat SPH?"
        description={
          confirmDel
            ? `Menghapus riwayat SPH tertanggal ${isoToDMY(confirmDel.date) || "—"}${confirmDel.note ? ` (${confirmDel.note})` : ""}. Aksi ini tidak dapat dibatalkan.`
            : ""
        }
        confirmLabel="Hapus"
        cancelLabel="Batal"
        danger
        onConfirm={doDelete}
        testid={`${testidPrefix}-submissions-confirm-delete`}
      />
    </div>
  );
}

// Helpers ---------------------------------------------------------------
function isoToDMY(iso) {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return "";
  return `${m[3]}/${m[2]}/${m[1]}`;
}

function todayISO() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Strip our transient UI-only fields (none currently, but keep as guard rail
// so future props like `_dirty` don't leak into payload).
function stripUi(r) {
  const { ...clean } = r;
  return clean;
}
